import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { SparkTextField } from 'sharedComponents/SparkForm';
import SparkSectionHeader from '../SparkSectionHeader';
import { useState, ChangeEvent } from 'react';
import { useFormikContext } from 'formik';
import { ImagesAndMediaFormValues } from './ImagesAndMediaForm';

export const getYouTubeVideoId = (url: string): string | null => {
  const youtubePatterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of youtubePatterns) {
    const match = url?.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

export const loomRegex =
  /https:\/\/www\.loom\.com\/share\/[a-zA-Z0-9_-]+(?:\?sid=[a-zA-Z0-9_-]+)?$/;

export const youtubeRegex =
  /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)/;

const VideoLoom = () => {
  const { values } = useFormikContext<ImagesAndMediaFormValues>();
  const [videoURL, setVideoURL] = useState<string>(values?.videoURL);
  const [videoEmbed, setVideoEmbed] = useState<JSX.Element | null>(null);
  const [error, setError] = useState<string>('');

  const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setVideoURL(url);

    if (getYouTubeVideoId(url) || loomRegex.test(url)) {
      setVideoEmbed(getVideoEmbed(url));
      setError('');
    } else {
      setVideoEmbed(null);
      setError('');
    }
  };

  React.useEffect(() => {
    if (values?.videoURL) {
      setVideoEmbed(getVideoEmbed(values.videoURL));
    }
  }, [values?.videoURL]);

  const getVideoEmbed = (url: string): JSX.Element | null => {
    const youtubeMatch = getYouTubeVideoId(url);
    if (youtubeMatch) {
      return (
        <iframe
          width='560'
          height='315'
          src={`https://www.youtube.com/embed/${youtubeMatch}?autoplay=1`}
          frameBorder='0'
          allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
          title='YouTube Video'
        />
      );
    }

    const loomMatch = url.match(loomRegex);
    if (loomMatch) {
      return (
        <iframe
          width='560'
          height='315'
          src={`https://www.loom.com/embed/${loomMatch[0].split('/').pop()}`}
          frameBorder='0'
          allow='autoplay; fullscreen; picture-in-picture'
          allowFullScreen
          title='Loom Video'
        />
      );
    }

    return null;
  };

  return (
    <Box>
      <Divider
        sx={{ width: '100%', marginBottom: '25px', marginTop: '25px' }}
      />
      <SparkSectionHeader
        headerText='Video / Loom'
        subHeaderText='Upload an optional video from YouTube or Loom to show people how to use your Spark or share your maker story. Video helps you connect with viewers.'
      />
      <SparkTextField
        size={12}
        label='Link to the video / loom'
        name='videoURL'
        placeholder='https://'
        value={videoURL}
        onChange={handleVideoChange}
      />

      {videoEmbed && (
        <Box display={'flex'} justifyContent={'center'}>
          {videoEmbed}
        </Box>
      )}

      {error && (
        <Box mt={2} textAlign='center'>
          <Typography color='error'>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default VideoLoom;
